<template>
  <!-- One candidate resume: the section grid, the type and the actions.
       Rendered inside the modal on /recruitment and on its own at /cv/<token>. -->
  <div class="cv-panel">
          <header class="cv-bar">
            <img v-svg-inline :data-src="require('@/assets/icons/logo.svg')" alt="Wdevs" class="cv-bar__logo" />
            <button v-if="closable" class="cv-bar__close" aria-label="Close" @click="$emit('close')">
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path d="M2 2l14 14M16 2 2 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
          </header>
  
          <div class="cv-doc" :style="cvStyle">
            <!-- Identity -->
            <p v-if="active.stack && !hidden('stack')" class="cv-eyebrow">{{ active.stack }}</p>
            <h2 class="cv-name">{{ displayName(active) }}</h2>
  
            <ul class="cv-facts">
              <li v-if="active.birthDate && !hidden('birthDate')"><span>Born</span>{{ active.birthDate }}</li>
              <li v-if="active.location && !hidden('location')"><span>Location</span>{{ active.location }}</li>
              <li v-if="active.timezone && !hidden('timezone')"><span>Time zone</span>{{ active.timezone }}</li>
              <li v-if="active.seniority && !hidden('seniority')"><span>Seniority</span>{{ active.seniority }}</li>
              <li v-if="active.experienceYears != null && !hidden('experienceYears')"><span>Experience</span>{{ active.experienceYears }} years</li>
              <li v-if="active.english && !hidden('english')"><span>English</span>{{ active.english }}</li>
              <li v-if="active.availability && !hidden('availability')"><span>Availability</span>{{ active.availability }}</li>
              <li v-if="!hidden('rate')" class="is-rate"><span>Rate</span>{{ rateLabel(active) }}</li>
            </ul>
  
            <p v-if="active.currentRole && !hidden('currentRole')" class="cv-role">{{ active.currentRole }}</p>
  
            <!-- Sections come from the profile's layout, so the admin controls
                 their order, their titles and which ones exist at all. -->
            <template v-for="(block, bi) in blocks" :key="bi">
              <div v-if="block.cols" class="cv-cols">
                <CvSection :entry="block.items[0]" :c="active" />
                <div class="cv-cols__side">
                  <CvSection v-for="e in block.items.slice(1)" :key="e.key" :entry="e" :c="active" />
                </div>
              </div>
              <CvSection v-else :entry="block.items[0]" :c="active" />
            </template>
  
            <!-- Profiles written before the structured CV existed keep their plain text -->
            <section v-if="active.cv && !hasStructuredCv(active)" class="cv-section">
              <h3 class="cv-section__title">About</h3>
              <p v-for="(t, i) in paragraphs(active.cv)" :key="i" class="cv-text">{{ t }}</p>
            </section>
          </div>
  
          <footer class="cv-actions">
            <button class="cv-actions__request" @click="$emit('request')">Request</button>
            <button class="cv-actions__share" :aria-label="shared ? 'Link copied' : 'Copy profile link'" @click="shareProfile">
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path d="M7 11a3 3 0 0 0 4.5.4l2.5-2.5a3 3 0 0 0-4.2-4.2l-1 1" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
                <path d="M11 7a3 3 0 0 0-4.5-.4L4 9.1a3 3 0 0 0 4.2 4.2l1-1" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
              </svg>
              <span>{{ shared ? 'Link copied' : 'Share' }}</span>
            </button>
          </footer>
  </div>
</template>

<script>
import CvSection from "@/components/recruitment/CvSection.vue";

// Fallback order for profiles saved before the layout editor existed.
const DEFAULT_LAYOUT = [
  { key: "SUMMARY", title: "Summary" },
  { key: "SKILLS", title: "Technical skills" },
  { key: "EXPERIENCE", title: "Experience" },
  { key: "EDUCATION", title: "Education" },
  { key: "CERTIFICATIONS", title: "Licenses & certifications" },
  { key: "LANGUAGES", title: "Languages" },
  { key: "PROJECTS", title: "Projects" },
  { key: "PERSONAL", title: "Personal skills" },
];
// These three render side by side while they stay adjacent in the layout.
const COLUMN_KEYS = ["EXPERIENCE", "EDUCATION", "CERTIFICATIONS"];
// Google font families already requested in this page load.
const loadedFonts = new Set();

export default {
  name: "CvDocument",
  components: { CvSection },
  props: {
    active: { type: Object, required: true },   // the record to render
    shareUrl: { type: String, default: "" },    // what the share button copies
    closable: { type: Boolean, default: false },
  },
  emits: ["close", "request"],
  data() {
    return { shared: false };
  },
  computed: {
    /**
     * Layout entries grouped into render blocks. Experience, education and
     * certifications sit side by side while they stay next to each other in the
     * order; move one away and it simply becomes a full-width section.
     */
    blocks() {
      const c = this.active;
      if (!c) return [];
      const layout = Array.isArray(c.layout) && c.layout.length ? c.layout : DEFAULT_LAYOUT;
      const twoCol = !c.appearance || c.appearance.twoColumnBackground !== false;
      const out = [];
      for (const entry of layout) {
        const prev = out[out.length - 1];
        const groupable = twoCol && COLUMN_KEYS.indexOf(entry.key) !== -1;
        if (groupable && prev && prev.cols && prev.items.length < 3) {
          prev.items.push(entry);
        } else if (groupable && prev && !prev.cols && COLUMN_KEYS.indexOf(prev.items[0].key) !== -1) {
          prev.cols = true;
          prev.items.push(entry);
        } else {
          out.push({ cols: false, items: [entry] });
        }
      }
      return out;
    },
    /** The appearance sheet as CSS custom properties on the document root. */
    cvStyle() {
      const a = (this.active && this.active.appearance) || {};
      const body = a.body || {};
      const name = a.name || {};
      const title = a.sectionTitle || {};
      const project = a.projectTitle || {};
      const font = a.fontFamily ? "'" + a.fontFamily + "'" : null;
      const heading = a.headingFont ? "'" + a.headingFont + "'" : font;
      const st = {};
      if (font) st["--cv-font"] = font + ", Helvetica, Arial, sans-serif";
      if (heading) st["--cv-heading-font"] = heading + ", Helvetica, Arial, sans-serif";
      if (a.accent) st["--cv-accent"] = a.accent;
      // Soft and tint shades of the same accent, for chips and tinted blocks.
      if (a.accent) {
        st["--cv-accent-soft"] = this.accentAlpha(a.accent, 0.35);
        st["--cv-accent-tint"] = this.accentAlpha(a.accent, 0.1);
      }
      if (body.size) st["--cv-body-size"] = body.size + "px";
      if (body.weight) st["--cv-body-weight"] = body.weight;
      if (body.align) st["--cv-body-align"] = body.align;
      if (name.size) st["--cv-name-size"] = name.size + "px";
      if (name.weight) st["--cv-name-weight"] = name.weight;
      if (name.align) st["--cv-name-align"] = name.align;
      if (title.size) st["--cv-title-size"] = title.size + "px";
      if (title.weight) st["--cv-title-weight"] = title.weight;
      if (title.align) st["--cv-title-align"] = title.align;
      if (project.size) st["--cv-project-size"] = project.size + "px";
      if (project.weight) st["--cv-project-weight"] = project.weight;
      if (project.align) st["--cv-project-align"] = project.align;
      return st;
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(c) { if (c) this.loadProfileFonts(c); },
    },
  },
  methods: {
    /** A header field the admin switched off for this profile. */
    hidden(key) {
      const list = this.active && this.active.appearance && this.active.appearance.hidden;
      return Array.isArray(list) && list.indexOf(key) !== -1;
    },
    displayName(c) {
      const initial = c.surname ? c.surname.trim().charAt(0).toUpperCase() + "." : "";
      return [c.name, initial].filter(Boolean).join(" ");
    },
    rateLabel(c) {
      if (!c.rate) return "Rate on request";
      const cur = !c.currency || c.currency === "USD" ? "$" : c.currency + " ";
      return cur + c.rate + "/hour";
    },
    /** Split stored text into paragraphs; a single line stays one paragraph. */
    paragraphs(text) {
      return String(text || "")
        .split(/\n+/)
        .map((t) => t.trim())
        .filter(Boolean);
    },
    hasBackground(c) {
      return !!((c.experience || []).length || (c.education || []).length || (c.certifications || []).length);
    },
    /** True once a profile carries any of the structured resume blocks. */
    hasStructuredCv(c) {
      return !!(c.summary || c.currentRole || (c.skills || []).length || (c.projects || []).length ||
        this.hasBackground(c) || (c.languages || []).length || c.personal || c.hobbies);
    },
    /** #rrggbb → rgba(), so one picked colour drives every tinted surface. */
    accentAlpha(hex, alpha) {
      const m = /^#([0-9a-f]{6})$/i.exec(String(hex || ""));
      if (!m) return "";
      const n = parseInt(m[1], 16);
      return "rgba(" + ((n >> 16) & 255) + "," + ((n >> 8) & 255) + "," + (n & 255) + "," + alpha + ")";
    },
    ensureFont(family) {
      if (!family || loadedFonts.has(family)) return;
      loadedFonts.add(family);
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=" +
        encodeURIComponent(family).replace(/%20/g, "+") +
        ":wght@300;400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    },
    loadProfileFonts(c) {
      const a = (c && c.appearance) || {};
      this.ensureFont(a.fontFamily);
      this.ensureFont(a.headingFont);
    },
    async shareProfile() {
      const url = this.shareUrl;
      try {
        await navigator.clipboard.writeText(url);
      } catch (e) {
        // The Clipboard API needs a secure context; fall back to a hidden field.
        const el = document.createElement("textarea");
        el.value = url;
        document.body.appendChild(el);
        el.select();
        try { document.execCommand("copy"); } catch (err) { /* nothing else to try */ }
        document.body.removeChild(el);
      }
      this.shared = true;
      setTimeout(() => { this.shared = false; }, 2000);
    },
  },
};
</script>

<style lang="scss">
@import "../../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";

// The document itself: a white sheet with a pinned header and action bar, the
// same inside the modal and on the standalone /cv/<token> page.
// Note on old browsers: the prerender Chromium has no flexbox gap, no inset
// and no :focus-visible, so spacing inside flex rows uses margins.
.cv-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  border-radius: 20px;
  background: $white;
  // Layered shadow: a tight contact edge plus a wide soft drop.
  box-shadow: 0 2px 6px rgba($black, 0.08), 0 40px 80px -32px rgba($black, 0.55);
}

// Header band with the company logo — sticks while the CV scrolls.
.cv-bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba($black, 0.08);
  border-radius: 20px 20px 0 0;
  background: $white;
  @include media(">=tablet") { padding: 18px 40px; }

  &__logo {
    display: block;
    width: 104px;
    height: auto;
    svg { display: block; width: 100%; height: auto; }
  }

  &__close {
    display: inline-flex;
    padding: 8px;
    border: 0;
    background: none;
    color: $grey;
    cursor: pointer;
    transition: color 0.2s ease, transform 0.2s ease;
    &:hover { color: $orange; transform: rotate(90deg); }
    &:focus { outline: 2px solid rgba($orange, 0.5); outline-offset: 2px; }
  }
}

.cv-doc {
  // Everything below reads the appearance sheet through these vars; each
  // fallback is the design default, so a profile without a sheet is unchanged.
  font-family: var(--cv-font, $primary-fonts);
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 28px 20px 28px;
  @include media(">=tablet") { padding: 36px 40px 36px; }
}

// ── identity ───────────────────────────────────────────────────────────────
.cv-eyebrow {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: var(--cv-name-align, left);
  color: var(--cv-accent, $orange);
}

.cv-name {
  margin-bottom: 16px;
  font-family: var(--cv-heading-font, inherit);
  font-size: var(--cv-name-size, 34px);
  font-weight: var(--cv-name-weight, 800);
  text-align: var(--cv-name-align, left);
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: $black;
  @include media(">=tablet") { font-size: var(--cv-name-size, 44px); }
}

.cv-facts {
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 20px -8px;
  padding: 0;
  list-style: none;

  li {
    margin: 0 0 8px 8px;
    padding: 7px 14px;
    border: 1px solid rgba($black, 0.1);
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    color: $black;

    span {
      margin-right: 6px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: $grey;
    }

    &.is-rate {
      border-color: var(--cv-accent-soft, rgba($orange, 0.35));
      background: var(--cv-accent-tint, rgba($orange, 0.07));
      span { color: var(--cv-accent, $darkOrange); }
    }
  }
}

.cv-role {
  margin-bottom: 8px;
  padding-left: 16px;
  border-left: 3px solid var(--cv-accent, $orange);
  font-size: 17px;
  font-weight: 600;
  line-height: 1.55;
  color: rgba($black, 0.88);
  @include media(">=tablet") { font-size: 18px; }
}

// ── sections ───────────────────────────────────────────────────────────────
.cv-section {
  padding-top: 28px;
  margin-top: 28px;
  border-top: 1px solid rgba($black, 0.08);

  &__title {
    margin-bottom: 16px;
    font-family: var(--cv-heading-font, inherit);
    font-size: var(--cv-title-size, 13px);
    font-weight: var(--cv-title-weight, 800);
    text-align: var(--cv-title-align, left);
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: $black;
  }
}

.cv-cols .cv-section:first-child,
.cv-cols__side .cv-section:first-child { border-top: 0; padding-top: 0; margin-top: 0; }

.cv-text {
  max-width: 68ch;
  margin-bottom: 14px;
  font-size: var(--cv-body-size, 16px);
  font-weight: var(--cv-body-weight, 400);
  text-align: var(--cv-body-align, left);
  line-height: 1.7;
  color: rgba($black, 0.82);
  b { font-weight: 700; color: $black; }
  &:last-child { margin-bottom: 0; }
}

// ── skills ─────────────────────────────────────────────────────────────────
.cv-skill {
  margin-bottom: 18px;
  &:last-child { margin-bottom: 0; }

  &__group {
    margin-bottom: 8px;
    font-size: 15px;
    font-weight: 700;
    color: $black;
  }
}

.cv-chips {
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 0 -6px;
  padding: 0;
  list-style: none;

  li {
    margin: 0 0 6px 6px;
    padding: 5px 12px;
    border-radius: 14px;
    background: $gray;
    font-size: 13px;
    font-weight: 600;
    color: rgba($black, 0.78);
  }
}

// ── two-column background block ────────────────────────────────────────────
.cv-cols {
  padding-top: 28px;
  margin-top: 28px;
  border-top: 1px solid rgba($black, 0.08);
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 28px;
  @include media(">=tablet") { grid-template-columns: 1fr 1fr; grid-gap: 40px; }

  &__side .cv-section + .cv-section { padding-top: 24px; margin-top: 24px; }
}

.cv-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    margin-bottom: 16px;
    padding-left: 16px;
    &:last-child { margin-bottom: 0; }

    // Timeline dot instead of a bullet glyph.
    &:before {
      content: "";
      position: absolute;
      top: 7px;
      left: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--cv-accent, $orange);
    }
  }

  &__head { margin: 0; font-size: 15px; font-weight: 700; color: $black; line-height: 1.4; }
  &__sub { margin: 3px 0 0; font-size: 14px; line-height: 1.5; color: rgba($black, 0.72); }
  &__when { margin: 4px 0 0; font-size: 13px; font-weight: 600; color: $grey; }
}

// ── languages ──────────────────────────────────────────────────────────────
.cv-langs {
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 0 -8px;
  padding: 0;
  list-style: none;

  li {
    display: inline-flex;
    align-items: center;
    margin: 0 0 8px 8px;
    padding: 8px 14px;
    border: 1px solid rgba($black, 0.1);
    border-radius: 20px;
    font-size: 15px;
    font-weight: 600;
    color: $black;

    b {
      margin-left: 8px;
      padding: 2px 8px;
      border-radius: 10px;
      background: var(--cv-accent-tint, rgba($orange, 0.12));
      font-size: 12px;
      font-weight: 800;
      color: var(--cv-accent, $darkOrange);
    }
  }
}

// ── projects ───────────────────────────────────────────────────────────────
.cv-project {
  padding: 22px;
  margin-bottom: 16px;
  border: 1px solid rgba($black, 0.09);
  border-radius: 14px;
  background: $white;
  &:last-child { margin-bottom: 0; }
  @include media(">=tablet") { padding: 26px 28px; }

  &__name {
    font-family: var(--cv-heading-font, inherit);
    font-size: var(--cv-project-size, 19px);
    font-weight: var(--cv-project-weight, 800);
    text-align: var(--cv-project-align, left);
    letter-spacing: -0.01em;
    line-height: 1.3;
    color: $black;
    @include media(">=tablet") { font-size: var(--cv-project-size, 21px); }
  }

  &__period {
    margin: 6px 0 14px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--cv-accent, $orange);
  }

  &__block { margin-top: 18px; }

  &__label {
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $grey;
  }

  &__extra {
    margin-top: 20px;
    padding: 16px 18px;
    border-radius: 10px;
    background: var(--cv-accent-tint, rgba($orange, 0.06));
    .cv-text { font-size: 15px; }
  }
}

.cv-bullets {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    margin-bottom: 8px;
    padding-left: 18px;
    font-size: 15px;
    line-height: 1.6;
    color: rgba($black, 0.82);
    &:last-child { margin-bottom: 0; }

    &:before {
      content: "";
      position: absolute;
      top: 9px;
      left: 2px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--cv-accent, $orange);
      opacity: 0.75;
    }
  }
}

// ── actions: pinned to the bottom of the panel, always reachable ───────────
.cv-actions {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid rgba($black, 0.08);
  border-radius: 0 0 20px 20px;
  background: $white;
  @include media(">=tablet") { padding: 18px 40px; }

  &__request {
    padding: 12px 30px;
    margin-right: 12px;
    border: 2px solid $orange;
    border-radius: 26px;
    background: $orange;
    color: $white;
    font-family: $primary-fonts;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.25s ease, border-color 0.25s ease, transform 0.1s ease;
    &:hover { background: $darkOrange; border-color: $darkOrange; }
    &:active { transform: translateY(1px); }
    &:focus { outline: 2px solid rgba($orange, 0.55); outline-offset: 2px; }
  }

  &__share {
    display: inline-flex;
    align-items: center;
    padding: 11px 18px;
    border: 1px solid rgba($black, 0.16);
    border-radius: 26px;
    background: $white;
    color: rgba($black, 0.75);
    font-family: $primary-fonts;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;

    svg { margin-right: 8px; }
    &:hover { color: $orange; border-color: rgba($orange, 0.5); background: rgba($orange, 0.06); }
    &:focus { outline: 2px solid rgba($orange, 0.55); outline-offset: 2px; }
  }
}

</style>